/**
 * 对话框布局类型
 */
import { atom, useAtomValue } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback } from 'react';
import useIsOU from './business/useIsOU';
import { useMutation } from '@tanstack/react-query';
import {
  useReceptionConfig,
  useSetReceptionConfig,
} from 'views/Setting/ConversationSetting/useReceptionConfig';
import { updateUserConfig } from 'services/api/admin';
import { orgConfigNewUIV2EnableState } from './business/useOrgConfig';
import posthog from '@leyan/lytics';

const CHAT_LAYOUT_TYPE_KEY = 'chatLayoutType';

const chatLayoutTypeState = atom<General.ConversationPageLayout>(
  (localStorage.getItem(CHAT_LAYOUT_TYPE_KEY) as General.ConversationPageLayout) || 'CLASSIC',
);

export const isLightChatLayoutState = atom<boolean>((get) => {
  const newUIV2Enable = get(orgConfigNewUIV2EnableState);
  if (newUIV2Enable) {
    return get(chatLayoutTypeState) === 'LIGHT';
  }

  return false;
});

export const useChatLayoutType = () => useAtomValue(chatLayoutTypeState);

export const useSetChatLayoutType = () => {
  return useAtomCallback(
    useCallback((_, set, newValue: General.ConversationPageLayout) => {
      set(chatLayoutTypeState, newValue);
      localStorage.setItem(CHAT_LAYOUT_TYPE_KEY, newValue);
      posthog.capture('$changeChatLayoutType', {
        chatLayoutType: newValue,
      });
    }, []),
  );
};

export const useIsLightChatLayout = () => useAtomValue(isLightChatLayoutState);

export const useUpdateChatLayoutType = () => {
  const isOU = useIsOU();
  const chatLayoutType = useChatLayoutType();
  const setChatLayoutType = useSetChatLayoutType();
  const receptionConfig = useReceptionConfig();
  const setReceptionConfig = useSetReceptionConfig();

  const updateReceptionConfig = useCallback(
    (newValue: General.ConversationPageLayout) => {
      setReceptionConfig((prev) => {
        if (prev) {
          return {
            ...prev,
            conversationPageLayout: newValue,
          };
        }
        return prev;
      });
    },
    [setReceptionConfig],
  );

  const { mutate: updateUserConfigMutation } = useMutation({
    mutationFn: (conversationPageLayout: General.ConversationPageLayout) => {
      if (receptionConfig) {
        const next = {
          ...receptionConfig,
          conversationPageLayout,
        };
        return updateUserConfig(next);
      }
      return Promise.reject(new Error('receptionConfig is not found'));
    },
  });

  const changeLayoutType = useCallback(
    (newValue: General.ConversationPageLayout) => {
      setChatLayoutType(newValue);
      updateReceptionConfig(newValue);
      if (isOU) {
        return;
      }
      updateUserConfigMutation(newValue);
    },
    [isOU, setChatLayoutType, updateReceptionConfig, updateUserConfigMutation],
  );

  return {
    chatLayoutType,
    changeLayoutType,
  };
};
